/**
 * Frontend release version helper.
 *
 * Reads the CalVer release version and git SHA that the deploy pipeline
 * inlines into each Vite bundle via `VITE_APP_VERSION` and `VITE_APP_GIT_SHA`
 * env vars. For local development (where those vars are unset), returns
 * sensible fallbacks so the helper is always safe to call.
 *
 * Use this anywhere a frontend zone needs to report its own release —
 * footer / about UI, console boot logs, error-tracking `release` config
 * (Sentry et al.), version-mismatch banners, etc. Always prefer this
 * helper over reading `import.meta.env.VITE_APP_VERSION` directly so the
 * shape and fallback semantics stay consistent across zones.
 *
 * Git tags remain the single source of truth for the version itself;
 * this helper only surfaces what the build pipeline stamped into the
 * bundle.
 *
 * @module lib/version
 */

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CALVER_PATTERN = /^\d{4}\.([1-9]|1[0-2])\.\d+$/;
const FALLBACK_VERSION = 'dev-local';
const FALLBACK_GIT_SHA = 'unknown';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AppVersion {
	/**
	 * CalVer release version (e.g. `2026.5.0`) for production/staging
	 * builds, or `dev-local` for local development builds where the
	 * pipeline did not inject `VITE_APP_VERSION`.
	 */
	version: string;
	/**
	 * Full commit SHA the bundle was built from, or `unknown` when the
	 * pipeline did not inject `VITE_APP_GIT_SHA`.
	 */
	gitSha: string;
	/**
	 * Short (7-character) form of {@link gitSha} for display, or
	 * `unknown` when no git SHA is available.
	 */
	shortSha: string;
	/**
	 * `true` when {@link version} matches the CalVer release pattern
	 * (`YYYY.M.PATCH`). Useful for gating release-only behaviour such as
	 * Sentry initialisation or "show in footer" UI without leaking dev
	 * builds into production telemetry.
	 */
	isReleaseBuild: boolean;
}

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

/**
 * Returns the current frontend bundle's release metadata.
 *
 * @example
 * ```ts
 * const { version, shortSha, isReleaseBuild } = getAppVersion();
 * if (isReleaseBuild) {
 *   console.info(`LearnAI v${version} (${shortSha})`);
 * }
 * ```
 */
export function getAppVersion(): AppVersion {
	const version = import.meta.env.VITE_APP_VERSION?.trim() || FALLBACK_VERSION;
	const gitSha = import.meta.env.VITE_APP_GIT_SHA?.trim() || FALLBACK_GIT_SHA;
	const shortSha = gitSha === FALLBACK_GIT_SHA ? gitSha : gitSha.slice(0, 7);
	const isReleaseBuild = CALVER_PATTERN.test(version);

	return { version, gitSha, shortSha, isReleaseBuild };
}
